import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Alert,
    MenuItem, // For Select dropdown
    Select,
    FormControl,
    InputLabel,
    Autocomplete,
    Box,
    Stack,
    Chip,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    IconButton
} from '@mui/material';
import ModalScaffold from './common/ModalScaffold';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CategoryIcon from '@mui/icons-material/Category';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { createTruck, updateTruck } from '../services/truckService';
import { truckStatusOptions, truckTypeOptions } from '../utils/constants'; // Define constants for status and type
import apiClient from '../api';
import { mapSamsaraVehicleType } from '../utils/samsaraVehicleType';

// Allowed truck makes for power units (Van/Flatbed)
const TRUCK_MAKES = [
    'Freightliner',
    'Peterbilt',
    'Kenworth',
    'International',
    'Volvo',
    'Mack',
    'Western Star',
    'Hino',
    'Isuzu',
    'Autocar',
    'Tesla',
    'Nikola',
    'BYD',
    'Mitsubishi Fuso',
    'Ford Pro',
    'Ram Commercial',
    'DAF (PACCAR-owned, limited U.S. presence)',
    'Severe Service International (Navistar sub-brand)',
    'Kalmar Ottawa (yard tractors)',
    'Capacity Trucks (yard / regional tractors)'
];

const initialFormData = {
    unitNumber: '',
    vin: '',
    make: '',
    model: '',
    customModel: '',
    year: '',
    customYear: '',
    type: 'Van', // Default type
    status: 'Active', // Default status
    notes: ''
};

function TruckForm({ open, onClose, truck, onSave, initialType, samsaraConnected = false }) {
    const [formData, setFormData] = useState(initialFormData);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [samsaraSyncOpen, setSamsaraSyncOpen] = useState(false);
    const [samsaraVehicles, setSamsaraVehicles] = useState([]);
    const [loadingSamsara, setLoadingSamsara] = useState(false);
    const isEditMode = Boolean(truck);
    const isTrailer = (formData.type === 'Trailer');
    const enableMakeDropdown = !isTrailer && ['van','flatbed'].includes(String(formData.type || '').toLowerCase());

    // Per-make model options (commonly used models in North America)
    const OTHER_OPTION = '__OTHER__';
    const MODEL_OPTIONS_BY_MAKE = {
        Freightliner: ['Cascadia', 'M2 106', 'M2 112', '122SD', '114SD', '108SD', 'Columbia', 'Coronado', 'Argosy', 'FLD120'],
        Peterbilt: ['579', '389', '379', '567', '367', '365', '520', '535', '548', '220'],
        Kenworth: ['T680', 'T880', 'W900', 'T800', 'T370', 'T440', 'T270', 'K270', 'C500', 'T660'],
        International: ['LT', 'ProStar', 'LoneStar', 'HX', 'HV', 'MV', 'RH', 'DuraStar', 'TranStar', 'CV'],
        Volvo: ['VNL 860', 'VNL 760', 'VNL 740', 'VNR 640', 'VNR 660', 'VNR 300', 'VNX 300', 'VNX 400', 'VHD 300', 'VHD 400'],
        Mack: ['Anthem', 'Pinnacle', 'Granite', 'MD6', 'MD7', 'CHU613', 'CXU613', 'Vision', 'LR', 'TerraPro'],
        'Western Star': ['49X', '47X', '5700XE', '4900', '4800', '6900', '4700', '6900XD', '5900', 'Star Light'],
        Hino: ['L6', 'L7', 'XL7', 'XL8', 'M5', 'M7', '268', '338', '258', '195'],
        Isuzu: ['NPR', 'NPR-HD', 'NQR', 'NRR', 'FTR', 'FVR', 'NLR', 'NMR', 'NPS', 'NPR-XD'],
        Autocar: ['DC-64', 'ACX (Xpeditor)', 'ACTT', 'ACMD', 'LEGACY Xpeditor', 'XPR', 'E-ACTT', 'E-ACX', 'E-ACTM', 'ISX'],
        Tesla: ['Semi'],
        Nikola: ['Tre BEV', 'Tre FCEV'],
        BYD: ['8TT', '6F', '6R', '6T', 'Q3', 'Q1', 'T10', 'T7', 'K7M', 'K9'],
        'Mitsubishi Fuso': ['Canter FE130', 'Canter FE140', 'Canter FE160', 'Canter FE180', 'Canter FG4X4', 'Rosa'],
        'Ford Pro': ['F-650', 'F-750', 'F-550 Chassis', 'F-600', 'Transit Cutaway', 'E-450', 'E-350', 'F-450 Chassis', 'F-350 Chassis', 'Transit'],
        'Ram Commercial': ['3500 Chassis Cab', '4500 Chassis Cab', '5500 Chassis Cab', 'ProMaster 3500', 'ProMaster 2500', 'ProMaster City', '3500', '4500', '5500', 'ProMaster Window'],
        DAF: ['XF', 'CF', 'LF', 'XG', 'XG+', 'XD', 'FT CF', 'FT XF', 'FA LF', 'FT XD'],
        'Severe Service International (Navistar sub-brand)': ['HX520', 'HX620', 'HV507', 'HV513', 'HV607', 'HV613', 'WorkStar', 'PayStar', '7600', '7700'],
        'Kalmar Ottawa (yard tractors)': ['T2 DOT/EPA', 'T2 Off-Highway', 'Electric T2E', '4x2 DOT', '6x4 DOT', '4x2 Off-Road', '6x4 Off-Road', 'T2E 4x2', 'T2 6x2', 'T2 LNG'],
        'Capacity Trucks (yard / regional tractors)': ['TJ5000', 'TJ6500', 'TJ7000', 'TJ9000', 'Sabre', 'TJ5000-E', 'TJ6500-E', 'TJ7000-E', 'TJ9000-E', 'TJ NGL']
    };

    const getModelOptions = (make) => MODEL_OPTIONS_BY_MAKE[make] || [];

    // Year options helper (currentYear+1 down to 1980)
    const CURRENT_YEAR = new Date().getFullYear();
    const MIN_YEAR = 1980;
    const MAX_YEAR = CURRENT_YEAR + 1;
    const yearOptions = Array.from({ length: MAX_YEAR - MIN_YEAR + 1 }, (_, i) => String(MAX_YEAR - i));
    const YEAR_OTHER_LABEL = 'Other';
    const isYearInOptions = (y) => (y !== '' && yearOptions.includes(String(y)));

    useEffect(() => {
        if (isEditMode && truck) {
            setFormData({ ...initialFormData, ...truck });
        } else {
            // Apply initialType for create mode if provided
            setFormData(prev => ({ ...initialFormData, type: initialType || initialFormData.type }));
        }
        setError(null);
    }, [truck, isEditMode, open, initialType]);

    // Load available Samsara vehicles
    const handleLoadSamsaraVehicles = async () => {
        if (!samsaraConnected) {
            setError('Samsara is not connected');
            return;
        }

        setLoadingSamsara(true);
        setSamsaraSyncOpen(true);
        try {
            const response = await apiClient.get('/trucks/samsara/available');
            if (response.data.success) {
                setSamsaraVehicles(response.data.vehicles || []);
            } else {
                setError('Failed to load Samsara vehicles');
            }
        } catch (err) {
            console.error('Error loading Samsara vehicles:', err);
            setError('Failed to load Samsara vehicles: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoadingSamsara(false);
        }
    };

    // Select a Samsara vehicle and auto-fill the form
    const handleSelectSamsaraVehicle = (vehicle) => {
        setFormData(prev => ({
            ...prev,
            unitNumber: vehicle.name || '',
            vin: vehicle.vin || '',
            make: vehicle.make || '',
            model: vehicle.model || '',
            year: vehicle.year ? String(vehicle.year) : '',
            type: mapSamsaraVehicleType(vehicle)
        }));
        setSamsaraSyncOpen(false);
        setSamsaraVehicles([]);
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNumberChange = (event) => {
        const { name, value } = event.target;
        setFormData(prev => ({
            ...prev,
            [name]: value === '' ? '' : Number(value)
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);

        const chosenModel = (enableMakeDropdown && formData.make && getModelOptions(formData.make).length > 0 && (!getModelOptions(formData.make).includes(formData.model)))
          ? (formData.customModel || formData.model)
          : formData.model;

        const chosenYear = isYearInOptions(formData.year)
          ? Number(formData.year)
          : (formData.customYear !== '' ? Number(formData.customYear) : null);
        if (chosenYear !== null && (chosenYear < 1900 || chosenYear > MAX_YEAR)) {
            setError(`Year must be between 1900 and ${MAX_YEAR}.`);
            setLoading(false);
            return;
        }

        const dataToSubmit = {
            ...formData,
            model: chosenModel,
            year: chosenYear,
            vin: (formData.vin || '').toUpperCase()
        };
        // For trailer type, strip non-required vehicle fields
        if (isTrailer) {
            if (!dataToSubmit.vin) delete dataToSubmit.vin;
            if (!dataToSubmit.make) delete dataToSubmit.make;
            if (!dataToSubmit.model) delete dataToSubmit.model;
            if (!dataToSubmit.year) delete dataToSubmit.year;
        }

        // Basic VIN length validation (can be more complex)
        if (dataToSubmit.vin && dataToSubmit.vin.length !== 17) {
            setError('VIN must be exactly 17 characters long.');
            setLoading(false);
            return;
        }

        try {
            if (isEditMode) {
                await updateTruck(truck._id, dataToSubmit);
            } else {
                await createTruck(dataToSubmit);
            }
            onSave();
            handleClose();
        } catch (err) {
            console.error('Submit error:', err);
            setError(err.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} truck.`);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData(initialFormData);
        setError(null);
        setLoading(false);
        onClose();
    };

    const typeColor = String(formData.type || '').toLowerCase() === 'van'
      ? 'primary'
      : String(formData.type || '').toLowerCase() === 'flatbed'
      ? 'warning'
      : 'secondary';

    return (
        <ModalScaffold
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          title={isEditMode ? 'Edit Unit' : 'Add New Unit'}
          subtitle="Enter core details to keep your fleet organized."
          icon={LocalShippingIcon}
          actions={
            <>
              <Button onClick={handleClose} disabled={loading} variant="outlined">Cancel</Button>
              <Button type="submit" form="truck-form" variant="contained" disabled={loading} sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)' }}>
                {loading ? <CircularProgress size={20} color="inherit" /> : (isEditMode ? 'Save Changes' : 'Create Unit')}
              </Button>
            </>
          }
        >
            <form id="truck-form" onSubmit={handleSubmit}>
                <Box sx={{ p: 0 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    
                    {/* Samsara Sync Button - only show in create mode when Samsara is connected */}
                    {!isEditMode && samsaraConnected && (
                        <Alert 
                            severity="info" 
                            sx={{ mb: 2 }}
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={handleLoadSamsaraVehicles}
                                    startIcon={<SyncIcon />}
                                    disabled={loadingSamsara}
                                >
                                    {loadingSamsara ? 'Loading...' : 'Import from Samsara'}
                                </Button>
                            }
                        >
                            Speed up data entry by importing vehicle details from Samsara
                        </Alert>
                    )}
                    
                    <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip size="small" icon={<CategoryIcon />} color={typeColor} label={`Type: ${formData.type || '—'}`} />
                        <Chip size="small" icon={<CheckCircleIcon />} variant="outlined" label={`Status: ${formData.status || '—'}`} />
                    </Stack>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', border: (theme) => `1px solid ${theme.palette.divider}` }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth margin="dense" variant="outlined" size="small">
                                <InputLabel id="type-label">Type</InputLabel>
                                <Select
                                    labelId="type-label"
                                    name="type"
                                    value={formData.type}
                                    onChange={handleChange}
                                    label="Type"
                                >
                                    {truckTypeOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                autoFocus={!isEditMode}
                                margin="dense"
                                name="unitNumber"
                                label="Unit Number"
                                type="text"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={formData.unitNumber}
                                onChange={handleChange}
                                required
                            />
                        </Grid>
                        {!isTrailer && (
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={[...yearOptions, YEAR_OTHER_LABEL]}
                                value={isYearInOptions(formData.year) ? String(formData.year) : (formData.customYear ? YEAR_OTHER_LABEL : '')}
                                onChange={(_, newValue) => {
                                    if (newValue === YEAR_OTHER_LABEL) {
                                        setFormData(prev => ({ ...prev, year: '', customYear: prev.customYear || '' }));
                                    } else if (newValue) {
                                        setFormData(prev => ({ ...prev, year: Number(newValue), customYear: '' }));
                                    } else {
                                        setFormData(prev => ({ ...prev, year: '', customYear: '' }));
                                    }
                                }}
                                renderInput={(params) => (
                                    <TextField {...params} margin="dense" label="Year" size="small" required />
                                )}
                                disableClearable={false}
                                ListboxProps={{ style: { maxHeight: 300 } }}
                                isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
                            />
                            {(!isYearInOptions(formData.year)) && (
                                <TextField
                                    margin="dense"
                                    name="customYear"
                                    label="Specify Year"
                                    type="number"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={formData.customYear}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setFormData(prev => ({ ...prev, customYear: v }));
                                    }}
                                    InputProps={{ inputProps: { min: 1900, max: MAX_YEAR, inputMode: 'numeric', pattern: '[0-9]*' } }}
                                    required
                                />
                            )}
                        </Grid>
                        )}
                        {!isTrailer && (
                         <Grid item xs={12}>
                             <TextField
                                margin="dense"
                                name="vin"
                                label="VIN (17 Characters)"
                                type="text"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={formData.vin}
                                onChange={handleChange}
                                inputProps={{ maxLength: 17, style: { textTransform: 'uppercase' } }}
                                required
                            />
                        </Grid>
                        )}
                        {!isTrailer && (
                        <Grid item xs={12} sm={6}>
                            {enableMakeDropdown ? (
                                <FormControl fullWidth margin="dense" variant="outlined" size="small" required>
                                    <InputLabel id="make-label">Make</InputLabel>
                                    <Select
                                        labelId="make-label"
                                        name="make"
                                        value={formData.make}
                                        onChange={handleChange}
                                        label="Make"
                                    >
                                        {TRUCK_MAKES.map((m) => (
                                            <MenuItem key={m} value={m}>{m}</MenuItem>
                                        ))}
                                        {formData.make && !TRUCK_MAKES.includes(formData.make) && (
                                            <MenuItem value={formData.make}>{formData.make}</MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            ) : (
                                <TextField
                                    margin="dense"
                                    name="make"
                                    label="Make"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={formData.make}
                                    onChange={handleChange}
                                    required
                                />
                            )}
                        </Grid>
                        )}
                        {!isTrailer && (
                        <Grid item xs={12} sm={6}>
                            {enableMakeDropdown && formData.make && getModelOptions(formData.make).length > 0 ? (
                                <>
                                  <FormControl fullWidth margin="dense" variant="outlined" size="small" required>
                                    <InputLabel id="model-label">Model</InputLabel>
                                    <Select
                                      labelId="model-label"
                                      name="model"
                                      value={getModelOptions(formData.make).includes(formData.model) ? formData.model : (formData.model ? OTHER_OPTION : '')}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        if (value === OTHER_OPTION) {
                                          setFormData(prev => ({ ...prev, model: '', customModel: prev.customModel || '' }));
                                        } else {
                                          setFormData(prev => ({ ...prev, model: value, customModel: '' }));
                                        }
                                      }}
                                      label="Model"
                                    >
                                      {getModelOptions(formData.make).map((m) => (
                                        <MenuItem key={m} value={m}>{m}</MenuItem>
                                      ))}
                                      <MenuItem value={OTHER_OPTION}>Other</MenuItem>
                                    </Select>
                                  </FormControl>
                                  {(!getModelOptions(formData.make).includes(formData.model) || !formData.model) && (
                                    <TextField
                                      margin="dense"
                                      name="customModel"
                                      label="Specify Model"
                                      type="text"
                                      fullWidth
                                      variant="outlined"
                                      size="small"
                                      value={formData.customModel || formData.model}
                                      onChange={(e) => setFormData(prev => ({ ...prev, customModel: e.target.value }))}
                                      required
                                    />
                                  )}
                                </>
                              ) : (
                                <TextField
                                    margin="dense"
                                    name="model"
                                    label="Model"
                                    type="text"
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    value={formData.model}
                                    onChange={handleChange}
                                    required
                                />
                              )}
                        </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                             <FormControl fullWidth margin="dense" variant="outlined" size="small">
                                <InputLabel id="status-label">Status</InputLabel>
                                <Select
                                    labelId="status-label"
                                    name="status"
                                    value={formData.status}
                                    onChange={handleChange}
                                    label="Status"
                                >
                                    {truckStatusOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                margin="dense"
                                name="notes"
                                label="Notes"
                                type="text"
                                fullWidth
                                multiline
                                rows={3}
                                variant="outlined"
                                size="small"
                                value={formData.notes}
                                onChange={handleChange}
                                placeholder="Optional: maintenance notes, specs, or identifiers"
                            />
                        </Grid>
                    </Grid>
                    </Box>
                </Box>
            </form>

            {/* Samsara Vehicle Selection Dialog */}
            <Dialog 
                open={samsaraSyncOpen} 
                onClose={() => {
                    setSamsaraSyncOpen(false);
                    setSamsaraVehicles([]);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Import from Samsara</Typography>
                        <IconButton onClick={() => {
                            setSamsaraSyncOpen(false);
                            setSamsaraVehicles([]);
                        }}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    {loadingSamsara ? (
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </Box>
                    ) : samsaraVehicles.length === 0 ? (
                        <Alert severity="info">
                            No new vehicles available from Samsara. All Samsara vehicles are already in your database.
                        </Alert>
                    ) : (
                        <>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Select a vehicle to auto-fill the form. Only vehicles not already in your database are shown.
                            </Typography>
                            <List>
                                {samsaraVehicles.map((vehicle, index) => (
                                    <ListItem key={vehicle.id || index} disablePadding>
                                        <ListItemButton onClick={() => handleSelectSamsaraVehicle(vehicle)}>
                                            <ListItemText
                                                primary={vehicle.name || `Vehicle ${vehicle.id}`}
                                                secondary={
                                                    <Box>
                                                        {vehicle.vin && <Typography variant="caption" display="block">VIN: {vehicle.vin}</Typography>}
                                                        {(vehicle.make || vehicle.model || vehicle.year) && (
                                                            <Typography variant="caption" display="block">
                                                                {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setSamsaraSyncOpen(false);
                        setSamsaraVehicles([]);
                    }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </ModalScaffold>
    );
}

export default TruckForm; 